// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';

// Local library
import {
  ICommuicationCallResponse,
  IGetAllEventsNameAndIdResponse,
  IGetCompressedCallResponse,
  IGetEventDetailsCallResponse,
  IGetEventsDetailsResponse,
  IGetRunnerResponse,
  IResponseModel,
  IToteProgramCallResponse
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';


/**
 * ToteProgram endpoints — events, races, runners, and pool metadata
 */
@Injectable()
export class ToteProgramAPIService {
  private readonly API_URL = '/api/ToteProgram';

  constructor(
    private readonly httpService: HttpService,
  ) {}


  /**
   * GET API
   */

  /**
   * Get runners whose name starts with the given prefix
   * @param {string} runnerPrefix Prefix string to search
   * @returns {Observable<IResponseModel<IGetRunnerResponse>>} Runner response
   */
  geRunners$(runnerPrefix: string): Observable<IResponseModel<IGetRunnerResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GeRunners`,
      params: this.httpService.objToHttpParams({
        runnerPrefix
      })
    });
  }

  /**
   * Get changes reported by Equibase for an event
   * @param {string} equibaseEventId Equibase event id
   * @param {string} raceDate Optional race date
   * @returns {Observable<IResponseModel<{}>>} Equibase changes
   */
  getEquibaseChanges$(equibaseEventId: string, raceDate?: string)
    // eslint-disable-next-line @typescript-eslint/ban-types
    : Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEquibaseChanges`,
      params: this.httpService.objToHttpParams({
        equibaseEventId,
        raceDate
      })
    });
  }

  /**
   * Get all current wagerable events (no race/runner/pool detail)
   * @returns {Observable<IResponseModel<IToteProgramCallResponse>>} Current events
   */
  getAllEvents$(): Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEvents`
    });
  }

  /**
   * Get events for a specific date (no race/runner/pool detail)
   * @param {string} requestedDateStr Date string in API format
   * @returns {Observable<IResponseModel<IToteProgramCallResponse>>} Events for the date
   */
  getAllEventsByDate$(requestedDateStr: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsByDate`,
      params: this.httpService.objToHttpParams({
        requestedDateStr
      })
    });
  }

  /**
   * Admin: get events for a specific date (no race/runner/pool detail)
   * @param {string} requestedDateStr Date string in API format
   * @returns {Observable<IResponseModel<IToteProgramCallResponse>>} Events for the date
   */
  getAllEventsByDateForAdmin$(requestedDateStr: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsByDateForAdmin`,
      params: this.httpService.objToHttpParams({
        requestedDateStr
      })
    });
  }

  /**
   * Get all tracks known by the system (for geolocation)
   * @returns {Observable<IResponseModel<{}>>} Track info
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getAllTracksForGeo$(): Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllTracksForGeo`,
    });
  }

  /**
   * Get trimmed event/race/pool data (non-wagering use only)
   * @param {string} eventId Event id
   * @returns {Observable<IResponseModel<{}>>} Lightweight event details
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getEventDetailLite$(eventId: string): Observable<IResponseModel<{}>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetailLite`,
      params: this.httpService.objToHttpParams({
        eventId
      })
    });
  }

  /**
   * Get all event names and ids the system has ever seen
   * @returns {Observable<IResponseModel<IGetAllEventsNameAndIdResponse>>} Names and ids
   */
  getAllEventsNameAndId$(): Observable<IResponseModel<IGetAllEventsNameAndIdResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetAllEventsNameAndId`,
    });
  }

  /**
   * Get full details (Races/Runners/Pools/…) for today's events
   * @param {string} runId Optional run identifier filter
   * @returns {Observable<IResponseModel<IGetEventsDetailsResponse[]>>} Detailed events
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getEventsDetails$(runId?: string): Observable<IResponseModel<IGetEventsDetailsResponse[]>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventsDetails`,
      params: this.httpService.objToHttpParams({
        runId,
        callOrigin: 0
      })
    });
  }

  /**
   * Compressed full details for today's events (Races/Runners/Pools/…)
   * @param {0|1|2} callOrigin Call origin (0|1|2)
   * @param {string} runId Optional run identifier filter
   * @returns {Observable<IResponseModel<IGetCompressedCallResponse>>} Compressed details
   */
  getCompressedEventsDetails$(callOrigin: 0|1|2= 0, runId?: string)
    : Observable<IResponseModel<IGetCompressedCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetCompressedEventsDetails`,
      params: this.httpService.objToHttpParams({
        callOrigin,
        runId
      })
    });
  }

  /**
   * Get detail (Races/Runners/Pools/…) for a specific event
   * @param {string} eventId Event id
   * @param {string} runId Run identifier
   * @param {0|1|2} callOrigin Call origin (0|1|2)
   * @returns {Observable<IResponseModel<IGetEventDetailsCallResponse>>} Event detail
   */
  getEventDetail$(eventId: string, runId: string, callOrigin: 0|1|2= 0)
    : Observable<IResponseModel<IGetEventDetailsCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetail`,
      params: this.httpService.objToHttpParams({
        eventId,
        runId,
        callOrigin
      })
    });
  }

  /**
   * Admin: get detail (Races/Runners/Pools/…) for a specific event
   * @param {string} eventId Event id
   * @param {string} runId Run identifier
   * @param {0|1|2} callOrigin Call origin (0|1|2)
   * @returns {Observable<IResponseModel<IGetEventDetailsCallResponse>>} Event detail
   */
  getEventDetailForAdmin$(eventId: string, runId: string, callOrigin: 0|1|2= 0)
    : Observable<IResponseModel<IGetEventDetailsCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetEventDetailForAdmin`,
      params: this.httpService.objToHttpParams({
        eventId,
        runId,
        callOrigin
      })
    });
  }

  /**
   * Get the current system state
   * @returns {Observable<IResponseModel<ICommuicationCallResponse>>} System state
   */
  getState$(): Observable<IResponseModel<ICommuicationCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetState`,
    });
  }

  /**
   * Get detail for a specific race (Runners/Pools/…)
   * @param {number} raceId Race id
   * @param {string} runId Run identifier
   * @param {string} eventId Event id
   * @returns {Observable<IResponseModel<IToteProgramCallResponse>>} Race detail
   */
  getRace$(raceId: number, runId: string, eventId: string)
    : Observable<IResponseModel<IToteProgramCallResponse>> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetRace`,
      params: this.httpService.objToHttpParams({
        raceId,
        runId,
        eventId
      })
    });
  }

}
